// Der Speicher aus dem Homelab-Protokoll (Topics `speicher.pool`, `.platte`,
// `.dataset`, `.dateisystem`, `.lage`; docs/protokoll.md › „Speicher über das
// Protokoll“) in genau die Form, die die Seiten aus der REST-Antwort
// GET /v2-api/storage kennen (`Storage` aus types.ts).
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/storage-protokoll.test.mjs) sie laden kann. Die Stände kommen skaliert
// an (`skaliere("speicher.pool", …)`, nur der Scan-Prozentwert ist
// skaliert); Gegenstück der Rechnung ist internal/protokoll/adapter/speicher.go.
//
// Regel, mit der die REST-Antwort exakt wiederkehrt (der Adapter sendet
// spiegelbildlich):
//   - Felder, die die REST-Antwort ohne `omitempty` führt, stehen immer:
//     fehlt der Wert im Stand, wird er ""/0/false (der Adapter lässt leere
//     Texte weg, Zahlen und Wahrheitswerte ohne omitempty sendet er immer);
//   - Felder mit `omitempty` (und Zeiger) stehen am Ergebnis nur, wenn der
//     Stand sie trägt -- ein Zeiger auf 0/false kommt als Wert an.

import type { Disk, Filesystem, PoolMember, PoolScan, ReplaceCandidate, Storage, ZfsDataset, ZfsPool } from "./types";
import type {
  SpeicherDateisystem,
  SpeicherDataset,
  SpeicherErsatz,
  SpeicherLage,
  SpeicherMitglied,
  SpeicherPlatte,
  SpeicherPool,
  SpeicherScan,
} from "./protokoll/schema.gen";

export type SpeicherArt = "pool" | "platte" | "dataset" | "dateisystem";

/** Instanzname → Art und Id: „speicher.pool.ab12…“ → { art: "pool", id }. Die
 *  Lage (`speicher.lage`) und alles andere → undefined. */
export function artAusInstanz(instanz: string): { art: SpeicherArt; id: string } | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== "speicher" || t[2] === "") return undefined;
  if (t[1] === "pool" || t[1] === "platte" || t[1] === "dataset" || t[1] === "dateisystem") return { art: t[1], id: t[2] };
  return undefined;
}

/** Schlüssel eines Eintrags in der Liste: Host und Name (bei Dateisystemen der
 *  Mountpoint). Das Zeichen dazwischen kommt in beiden nicht vor. */
export function speicherSchluessel(host: string, name: string): string {
  return host + "\u0000" + name;
}

// Wert, wenn vorhanden (nicht null/undefined), sonst Rückfall.
const s = (v: string | null | undefined): string => v ?? "";
const n = (v: number | null | undefined): number => v ?? 0;
const b = (v: boolean | null | undefined): boolean => v ?? false;

/** Setzt `o[k]`, wenn der Stand einen Wert trägt (auch 0/false/""). */
function wenn<T extends object, K extends keyof T>(o: T, k: K, v: T[K] | null | undefined): void {
  if (v != null) o[k] = v as T[K];
}

function mitglied(m: SpeicherMitglied): PoolMember {
  const o: PoolMember = { name: s(m.name), state: s(m.zustand), label: s(m.label) };
  wenn(o, "path", m.pfad);
  wenn(o, "guid", m.guid);
  wenn(o, "read", m.lesefehler);
  wenn(o, "write", m.schreibfehler);
  wenn(o, "cksum", m.pruefsummenfehler);
  wenn(o, "group", m.gruppe);
  wenn(o, "resilvering", m.resilver);
  wenn(o, "note", m.notiz);
  wenn(o, "disk", m.platte);
  wenn(o, "serial", m.seriennummer);
  return o;
}

function scan(c: SpeicherScan): PoolScan {
  const o: PoolScan = { kind: s(c.art), running: b(c.laeuft), text: s(c.text) };
  wenn(o, "percent", c.prozent);
  wenn(o, "to_go", c.rest);
  wenn(o, "rate", c.tempo);
  wenn(o, "amount", c.menge);
  return o;
}

function ersatz(e: SpeicherErsatz): ReplaceCandidate {
  const o: ReplaceCandidate = {
    disk: s(e.platte),
    size: n(e.groesse),
    has_data: b(e.hat_daten),
    member: s(e.mitglied),
    member_label: s(e.mitglied_label),
  };
  wenn(o, "serial", e.seriennummer);
  wenn(o, "model", e.modell);
  wenn(o, "partitions", e.partitionen);
  if (e.signaturen != null) o.signatures = [...e.signaturen];
  wenn(o, "smart_warning", e.smart_warnung);
  return o;
}

/** Der Pool aus seinem Stand, wie ihn die REST-Antwort trägt. */
export function poolAusProtokoll(p: SpeicherPool): ZfsPool {
  const o: ZfsPool = {
    name: s(p.name),
    size: n(p.groesse),
    alloc: n(p.belegt),
    free: n(p.frei),
    frag: s(p.frag),
    health: s(p.zustand),
    usable_size: n(p.nutzbar_groesse),
    usable_used: n(p.nutzbar_belegt),
    usable_free: n(p.nutzbar_frei),
    overhead: n(p.overhead),
    host: s(p.host),
    host_id: s(p.host_id),
    cluster: s(p.cluster),
  };
  if (p.mitglieder != null) o.members = p.mitglieder.map(mitglied);
  if (p.scan != null) o.scan = scan(p.scan);
  wenn(o, "status_text", p.status_text);
  wenn(o, "action_text", p.action_text);
  if (p.ersatz != null) o.replace_candidates = p.ersatz.map(ersatz);
  return o;
}

/** Die Platte aus ihrem Stand (ohne smart_json, das die Liste auch nicht trägt). */
export function platteAusProtokoll(p: SpeicherPlatte): Disk {
  const o: Disk = {
    name: s(p.name),
    size: n(p.groesse),
    rotational: b(p.rotierend),
    transport: s(p.anschluss),
    model: s(p.modell),
    health: s(p.zustand),
    temp: s(p.temp),
    pool: s(p.pool),
    host: s(p.host),
    host_id: s(p.host_id),
    cluster: s(p.cluster),
    is_nvme: b(p.nvme),
  };
  wenn(o, "serial", p.seriennummer);
  wenn(o, "firmware", p.firmware);
  wenn(o, "power_on_hours", p.betriebsstunden);
  wenn(o, "bytes_written", p.geschrieben);
  wenn(o, "percent_used", p.verschleiss);
  wenn(o, "reallocated_sectors", p.umgelagert);
  wenn(o, "pending_sectors", p.schwebend);
  wenn(o, "offline_uncorrectable", p.offline_unkorrigierbar);
  wenn(o, "reported_uncorrect", p.gemeldet_unkorrigierbar);
  wenn(o, "crc_errors", p.crc_fehler);
  wenn(o, "selftest_status", p.selbsttest_status);
  wenn(o, "selftest_passed", p.selbsttest_ok);
  wenn(o, "selftest_hours_ago", p.selbsttest_vor_h);
  wenn(o, "selftest_aborted", p.selbsttest_abbruch);
  wenn(o, "selftest_aborted_hours_ago", p.selbsttest_abbruch_vor_h);
  wenn(o, "selftest_running", p.selbsttest_laeuft);
  wenn(o, "selftest_remaining_percent", p.selbsttest_rest);
  wenn(o, "prefail_state", p.prefail);
  wenn(o, "partitions", p.partitionen);
  if (p.signaturen != null) o.signatures = [...p.signaturen];
  wenn(o, "in_use", p.belegt);
  return o;
}

/** Das Dataset aus seinem Stand. */
export function datasetAusProtokoll(d: SpeicherDataset): ZfsDataset {
  return {
    name: s(d.name),
    used: n(d.belegt),
    avail: n(d.verfuegbar),
    refer: n(d.referenziert),
    mountpoint: s(d.mountpoint),
    host: s(d.host),
    cluster: s(d.cluster),
  };
}

/** Das Dateisystem aus seinem Stand. */
export function dateisystemAusProtokoll(f: SpeicherDateisystem): Filesystem {
  return {
    source: s(f.quelle),
    mountpoint: s(f.mountpoint),
    size: n(f.groesse),
    used: n(f.belegt),
    avail: n(f.verfuegbar),
    host: s(f.host),
    cluster: s(f.cluster),
  };
}

/** Die Felder der Antwort, die nicht in einer Liste stehen: Zeit und Ergebnis
 *  des letzten Collector-Laufs und die Standard-Schwelle. */
export type Lage = Required<Pick<Storage, "last_refresh_ok" | "schwelle_standard">> & Pick<Storage, "last_refresh">;

/** Die Lage aus ihrem Stand. `last_refresh` fehlt, solange der Collector noch
 *  nie gelaufen ist (die REST-Antwort trägt dann eine Null-Zeit, die die Seite
 *  ohnehin als „nie“ liest). */
export function lageAusProtokoll(l: SpeicherLage): Lage {
  const o: Lage = {
    last_refresh_ok: b(l.ok),
    schwelle_standard: { warn: n(l.warn_pct), krit: n(l.krit_pct) },
  };
  if (typeof l.zuletzt === "number" && Number.isFinite(l.zuletzt)) o.last_refresh = new Date(l.zuletzt).toISOString();
  return o;
}

/** Tiefer Vergleich von JSON-Werten (Zahlen, Texte, Listen, einfache Objekte). */
export function gleich(a: unknown, b2: unknown): boolean {
  if (a === b2) return true;
  if (typeof a !== "object" || typeof b2 !== "object" || a === null || b2 === null) return false;
  if (Array.isArray(a) !== Array.isArray(b2)) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b2);
  if (ka.length !== kb.length) return false;
  const rb = b2 as Record<string, unknown>;
  const ra = a as Record<string, unknown>;
  return ka.every((k) => k in rb && gleich(ra[k], rb[k]));
}

/** Übernimmt `neu` IN-PLACE in `ziel` (Objekt-Identität bleibt, damit nichts
 *  zappelt): geänderte Felder setzen, Unverändertes in Ruhe lassen (ein
 *  reaktives Objekt meldet sonst auch gleiche Listen und Teilobjekte als
 *  geändert), Felder, die `neu` nicht trägt, entfernen -- Object.assign allein
 *  nähme sie nicht zurück. Liefert, ob sich etwas geändert hat. */
export function uebernimm<T extends object>(ziel: T, neu: T): boolean {
  const z = ziel as Record<string, unknown>;
  const q = neu as Record<string, unknown>;
  let geaendert = false;
  for (const k of Object.keys(q)) {
    if (!gleich(z[k], q[k])) {
      z[k] = q[k];
      geaendert = true;
    }
  }
  for (const k of Object.keys(z)) {
    if (!(k in q)) {
      delete z[k];
      geaendert = true;
    }
  }
  return geaendert;
}

/** Der jüngste Protokoll-Stand eines Eintrags mit der laufenden Nummer seines
 *  Eintreffens; `e: null` = der Eintrag ist weg (Grabstein). */
export interface Gemerkt<T> {
  e: T | null;
  nr: number;
}

/** Die Liste einer Art aus einer REST-Antwort (`rest`), mit den
 *  Protokoll-Ständen darüber, die **während** des Abrufs kamen (Nummer > `ab`):
 *  die Antwort kann hinter ihnen liegen. Ältere kannte der Server beim Bauen der
 *  Antwort schon (und nach einer Unterbrechung des Protokolls wären sie
 *  veraltet). Ein Grabstein jüngerer Nummer nimmt den Eintrag heraus, den die
 *  Antwort noch nennt; ein Eintrag, den nur ein jüngerer Stand kennt (kam
 *  während des Abrufs dazu), hängt sich hinten an. Die Reihenfolge der Antwort
 *  bleibt. */
export function mitStaenden<T>(rest: readonly T[], gemerkt: ReadonlyMap<string, Gemerkt<T>>, ab: number, schluessel: (e: T) => string): T[] {
  const kommt = new Set<string>();
  const alle: T[] = [];
  for (const e of rest) {
    const k = schluessel(e);
    kommt.add(k);
    const g = gemerkt.get(k);
    if (!g || g.nr <= ab) alle.push(e);
    else if (g.e) alle.push(g.e);
  }
  for (const [k, g] of gemerkt) if (!kommt.has(k) && g.nr > ab && g.e) alle.push(g.e);
  return alle;
}
